import React, { useEffect, useRef, useState } from 'react';

import { AnyClip, ClipFraming, ProjectAsset, TrackKind, VideoClip, AudioClip } from '../../types/editor';
import { MIN_CLIP_DUR_FOR_SPLIT } from './editorState';

interface ClipContextMenuProps {
  /** Viewport coords where the user right-clicked. */
  x: number;
  y: number;
  track: TrackKind;
  clip: AnyClip;
  /** All clips on the same track, ordered by start. Used to find the
   *  previous / next neighbor for the hug actions. */
  trackClips: AnyClip[];
  /** Source asset (for the "Double length" cap on video/audio). */
  asset: ProjectAsset | undefined;
  /** Active aspect's canvas dimensions, used by the Fill Width /
   *  Fill Height actions to compute the framing scale that makes
   *  the source touch the canvas edge. */
  canvasW: number;
  canvasH: number;
  onClose: () => void;
  onMoveClip: (patch: Partial<AnyClip>) => void;
  onTrimClip: (patch: Partial<AnyClip>) => void;
  onSplitClip: () => void;
  onDeleteClip: () => void;
  /** Replace the framing for THIS clip on the active aspect. The
   *  Timeline owns the aspect routing so the menu just supplies a
   *  full framing record. */
  onSetFraming: (patch: ClipFraming) => void;
}

function clipDur(c: AnyClip): number { return c.trimOut - c.trimIn; }

/**
 * Right-click menu for a timeline clip. Renders as a fixed-positioned
 * floating panel near the click point, with five actions:
 *
 *  - Hug Last Clip: nudge start so this clip butts up against the
 *    end of the previous clip on the same track.
 *  - Hug Next Clip: nudge start so this clip's end butts up against
 *    the start of the next clip.
 *  - Double length: doubles the clip's duration (capped at the
 *    source media duration for video / audio; uncapped for images).
 *  - Split in half: splits the clip into two equal halves.
 *  - Remove Clip: deletes the clip (with confirmation modal).
 *
 * Click outside or Esc closes the menu. The actions dispatch through
 * the parent — the menu itself stays presentation-only.
 */
export default function ClipContextMenu({
  x, y, track, clip, trackClips, asset, canvasW, canvasH, onClose,
  onMoveClip, onTrimClip, onSplitClip, onDeleteClip, onSetFraming,
}: ClipContextMenuProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  /* Confirm modal for Remove Clip — same UX as the inspector's
   * Remove button. Keeps the destructive action behind one extra tap. */
  const [confirmRemove, setConfirmRemove] = useState(false);

  /* Click outside dismisses. Listens on document mousedown so the
   * dismiss fires even when the user clicks a different menu, the
   * timeline body, or any other interactive surface. */
  useEffect(() => {
    function onDocDown(e: MouseEvent) {
      if (confirmRemove) return; // confirmation owns the dismiss
      const el = panelRef.current;
      if (el && !el.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (confirmRemove) setConfirmRemove(false);
        else onClose();
      }
    }
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, confirmRemove]);

  /* Clamp the panel inside the viewport so it doesn't bleed off-screen
   * when the user right-clicks near the bottom/right edge. */
  const PANEL_W = 200;
  const PANEL_H = 340;
  const clampedX = Math.min(Math.max(8, x), window.innerWidth - PANEL_W - 8);
  const clampedY = Math.min(Math.max(8, y), window.innerHeight - PANEL_H - 8);

  // ---- action implementations ----

  const dur = clipDur(clip);

  /* Hug Last: find the latest clip whose END is at or before this
   * clip's start. That clip's end becomes this clip's new start. */
  function hugLast(): void {
    const prev = trackClips
      .filter((c) => c.id !== clip.id && c.start + clipDur(c) <= clip.start + 0.001)
      .sort((a, b) => (b.start + clipDur(b)) - (a.start + clipDur(a)))[0];
    if (!prev) return;
    onMoveClip({ start: Math.max(0, prev.start + clipDur(prev)) });
    onClose();
  }

  /* Hug Next: find the earliest clip whose START is at or after this
   * clip's end. This clip's new start = nextClip.start - dur. */
  function hugNext(): void {
    const next = trackClips
      .filter((c) => c.id !== clip.id && c.start >= clip.start + dur - 0.001)
      .sort((a, b) => a.start - b.start)[0];
    if (!next) return;
    onMoveClip({ start: Math.max(0, next.start - dur) });
    onClose();
  }

  /* Double length: extend trimOut to 2x current duration, capped by
   * source media duration when there is one. Images have no source
   * cap (still images can loop indefinitely). */
  function doubleLength(): void {
    const newDur = dur * 2;
    let newTrimOut = clip.trimIn + newDur;
    const isMediaWithDur = asset && asset.duration && asset.kind !== 'image';
    if (isMediaWithDur) {
      newTrimOut = Math.min(newTrimOut, asset.duration as number);
    }
    if (newTrimOut <= clip.trimOut + 0.001) return;
    onTrimClip({ trimOut: newTrimOut });
    onClose();
  }

  function splitInHalf(): void {
    if (dur < MIN_CLIP_DUR_FOR_SPLIT) return;
    onSplitClip();
    onClose();
  }

  /* Fill Width / Fill Height: compute the framing scale that makes
   * the fit-inside picture touch the canvas edge along one axis. The
   * "contain" fit gives a (dw, dh) rectangle whose larger dimension
   * equals the canvas's larger dimension; multiplying both by
   * (canvasW / fit.dw) makes WIDTH fill the canvas (any vertical
   * overflow gets cropped by the renderer's window). Same logic for
   * height. Resets offsets to 0 — the user explicitly asked for a
   * filled frame, not an offset one. */
  function computeFitDims(): { dw: number; dh: number } | null {
    const sw = asset?.width;
    const sh = asset?.height;
    if (!sw || !sh || !canvasW || !canvasH) return null;
    const fit = Math.min(canvasW / sw, canvasH / sh);
    return { dw: sw * fit, dh: sh * fit };
  }

  function fillWidth(): void {
    const fit = computeFitDims();
    if (!fit || fit.dw <= 0) return;
    onSetFraming({ scale: canvasW / fit.dw, offsetX: 0, offsetY: 0 });
    onClose();
  }

  function fillHeight(): void {
    const fit = computeFitDims();
    if (!fit || fit.dh <= 0) return;
    onSetFraming({ scale: canvasH / fit.dh, offsetX: 0, offsetY: 0 });
    onClose();
  }

  const fitDims = computeFitDims();
  /* Enable Fill only when we know the source dims AND the fill scale
   * would actually change something (avoid no-op on already-filled
   * dimensions, where fit.dw === canvasW or fit.dh === canvasH). */
  const canFillWidth = !!fitDims && Math.abs(fitDims.dw - canvasW) > 0.5;
  const canFillHeight = !!fitDims && Math.abs(fitDims.dh - canvasH) > 0.5;

  // ---- enable / disable gates ----

  const canHugLast = trackClips.some(
    (c) => c.id !== clip.id && c.start + clipDur(c) <= clip.start + 0.001,
  );
  const canHugNext = trackClips.some(
    (c) => c.id !== clip.id && c.start >= clip.start + dur - 0.001,
  );
  const isMediaWithDur = !!(asset && asset.duration && asset.kind !== 'image');
  const canDouble = !isMediaWithDur
    || ((asset?.duration as number) - clip.trimOut) > 0.05
    || (clip.trimIn + dur * 2) <= (asset?.duration as number);
  const canSplit = dur >= MIN_CLIP_DUR_FOR_SPLIT;

  return (
    <>
      {/* Hide the menu (don't unmount) while the confirmation is up
          so it doesn't sit visibly behind the modal. The menu's local
          state (confirmRemove) lives on this component, so unmounting
          would tear down the confirmation too — visibility-hidden is
          the right tradeoff. */}
      <div
        ref={panelRef}
        className="clip-context-menu"
        role="menu"
        aria-label="Clip actions"
        style={{
          left: clampedX,
          top: clampedY,
          visibility: confirmRemove ? 'hidden' : 'visible',
        }}
        onContextMenu={(e) => e.preventDefault()}
      >
        <button
          type="button"
          className="clip-context-menu__item"
          role="menuitem"
          disabled={!canHugLast}
          onClick={hugLast}
        >
          Hug Last Clip
        </button>
        <button
          type="button"
          className="clip-context-menu__item"
          role="menuitem"
          disabled={!canHugNext}
          onClick={hugNext}
        >
          Hug Next Clip
        </button>
        {track === 'video' && (
          <>
            <div className="clip-context-menu__sep" role="separator" />
            <button
              type="button"
              className="clip-context-menu__item"
              role="menuitem"
              disabled={!canFillWidth}
              onClick={fillWidth}
            >
              Fill Width
            </button>
            <button
              type="button"
              className="clip-context-menu__item"
              role="menuitem"
              disabled={!canFillHeight}
              onClick={fillHeight}
            >
              Fill Height
            </button>
          </>
        )}
        <div className="clip-context-menu__sep" role="separator" />
        <button
          type="button"
          className="clip-context-menu__item"
          role="menuitem"
          disabled={!canDouble}
          onClick={doubleLength}
        >
          Double length
        </button>
        <button
          type="button"
          className="clip-context-menu__item"
          role="menuitem"
          disabled={!canSplit}
          onClick={splitInHalf}
        >
          Split in half
        </button>
        <div className="clip-context-menu__sep" role="separator" />
        <button
          type="button"
          className="clip-context-menu__item clip-context-menu__item--danger"
          role="menuitem"
          onClick={() => setConfirmRemove(true)}
        >
          Remove Clip
        </button>
      </div>

      {/* Confirmation overlay — mirrors the inspector's Remove flow so
          destructive actions feel consistent across surfaces. */}
      {confirmRemove && (
        <div
          className="modal modal--centered modal--confirm"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm remove"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setConfirmRemove(false);
          }}
        >
          <div className="modal__panel modal__panel--narrow">
            <header className="modal__head">
              <h3>Remove clip?</h3>
            </header>
            <div className="modal__body">
              <p>Are you sure you want to remove this clip from the timeline?</p>
            </div>
            <footer className="modal__foot">
              <button
                type="button"
                className="ghost"
                onClick={() => setConfirmRemove(false)}
              >
                No
              </button>
              <button
                type="button"
                className="danger danger--strong"
                onClick={() => {
                  setConfirmRemove(false);
                  onDeleteClip();
                  onClose();
                }}
              >
                Yes, remove
              </button>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}

/* Re-export types the consumer needs so the parent can declare local
 * state without re-importing AnyClip / TrackKind from the editor
 * types module. */
export type { AnyClip, TrackKind, VideoClip, AudioClip };
